'use client';

import { useState, Suspense } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/app/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase-production.config';
import { getObraRedirectUrl } from '@/lib/obra-redirect';
import { Obra } from '@/types/obra';

const loginSchema = z.object({
  email: z
    .string()
    .min(1, 'El correo electrónico es obligatorio')
    .email('Por favor ingrese un correo electrónico válido'),
  password: z
    .string()
    .min(1, 'La contraseña es obligatoria')
    .min(6, 'La contraseña debe tener al menos 6 caracteres'),
  rememberMe: z.boolean().default(false),
});

type LoginFormData = z.infer<typeof loginSchema>;

function LoginFormInner() {
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();
  const searchParams = useSearchParams();
  const redirectTo = searchParams.get('redirectTo') || null;

  const { signIn, clearError, error, userProfile } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<LoginFormData>({
    // rememberMe uses .default(false): zod v4 makes it optional on the input
    // side and required on the output side, so zodResolver's type does not
    // match useForm<LoginFormData>. Runtime behaviour is unchanged.
    resolver: zodResolver(loginSchema) as Resolver<LoginFormData>,
    defaultValues: {
      rememberMe: false,
    },
  });

  // Helper function to fetch user's obras after login
  const fetchUserObras = async (userId: string, userRole: string): Promise<Obra[]> => {
    try {
      let q;
      if (userRole === 'admin') {
        // Admin gets all obras
        q = query(collection(db, 'obras'));
      } else {
        // Non-admin gets only assigned obras
        q = query(collection(db, 'obras'), where('assignedUsers', 'array-contains', userId));
      }

      const snapshot = await getDocs(q);
      return snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          name: data.name,
          description: data.description,
          status: data.status,
          clientId: data.clientId,
          managerId: data.managerId,
          currency: data.currency,
          startDate: data.startDate?.toDate(),
          expectedEndDate: data.expectedEndDate?.toDate(),
          actualEndDate: data.actualEndDate?.toDate(),
          progress: data.progress || 0,
          sectors: data.sectors || [],
          plans: data.plans || [],
          assignedUsers: data.assignedUsers || [],
          createdBy: data.createdBy,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } as Obra;
      });
    } catch (error) {
      console.error('Error fetching user obras:', error);
      return [];
    }
  };

  const onSubmit = async (data: LoginFormData) => {
    try {
      setIsLoading(true);
      clearError();

      await signIn(data.email, data.password);

      console.log('✅ Sign in successful, attempting redirect...');
      toast.success('¡Bienvenido de nuevo!');

      // If there's a specific redirect URL, use that
      if (redirectTo) {
        console.log('📍 Using redirectTo:', redirectTo);
        router.push(redirectTo);
        return;
      }

      // For default redirect, check if user should go to single obra
      // We need to wait a moment for userProfile to be available
      setTimeout(async () => {
        const currentUserProfile = userProfile;
        if (!currentUserProfile) {
          console.log('📍 No user profile yet, using default redirect: /obras-list');
          router.push('/obras-list');
          return;
        }

        try {
          // Fetch user's obras to determine redirect
          const obras = await fetchUserObras(currentUserProfile.id, currentUserProfile.role);
          const redirectUrl = getObraRedirectUrl(currentUserProfile, obras);

          if (redirectUrl) {
            console.log('📍 Redirecting to single obra:', redirectUrl);
            router.push(redirectUrl);
          } else {
            console.log('📍 Using default redirect: /obras-list');
            router.push('/obras-list');
          }
        } catch (error) {
          console.error('Error determining redirect:', error);
          router.push('/obras-list');
        }
      }, 100);
    } catch (err) {
      console.error('Login error:', err);

      // Handle specific Firebase auth errors
      if (err instanceof Error && 'code' in err) {
        const firebaseError = err as { code?: string; message?: string };
        if (firebaseError.code === 'auth/user-not-found') {
          setError('email', {
            message: 'No se encontró ninguna cuenta con este correo electrónico',
          });
        } else if (firebaseError.code === 'auth/wrong-password') {
          setError('password', { message: 'Contraseña incorrecta' });
        } else if (firebaseError.code === 'auth/invalid-email') {
          setError('email', { message: 'Por favor ingrese un correo electrónico válido' });
        } else if (firebaseError.code === 'auth/user-disabled') {
          toast.error('Su cuenta ha sido deshabilitada. Por favor contacte al soporte.');
        } else if (firebaseError.code === 'auth/too-many-requests') {
          toast.error(
            'Demasiados intentos de inicio de sesión fallidos. Inténtelo de nuevo más tarde.'
          );
        } else {
          toast.error(
            firebaseError.message || 'Error de inicio de sesión. Por favor inténtelo de nuevo.'
          );
        }
      } else {
        toast.error('Error de inicio de sesión. Por favor inténtelo de nuevo.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-center">Iniciar Sesión</CardTitle>
          <CardDescription className="text-center text-gray-600">
            Ingrese sus credenciales para acceder a su cuenta
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Correo Electrónico</Label>
              <Input
                id="email"
                type="email"
                placeholder="su@correo.com"
                autoComplete="email"
                disabled={isLoading}
                {...register('email')}
                className={errors.email ? 'border-red-500' : ''}
              />
              {errors.email && <p className="text-sm text-red-500">{errors.email.message}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Contraseña</Label>
              <PasswordInput
                id="password"
                placeholder="Ingrese su contraseña"
                autoComplete="current-password"
                disabled={isLoading}
                {...register('password')}
                className={errors.password ? 'border-red-500' : ''}
              />
              {errors.password && <p className="text-sm text-red-500">{errors.password.message}</p>}
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <input
                  id="rememberMe"
                  type="checkbox"
                  disabled={isLoading}
                  {...register('rememberMe')}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <Label htmlFor="rememberMe" className="text-sm">
                  Recordarme
                </Label>
              </div>
              <Link href="/forgot-password" className="text-sm text-blue-600 hover:underline">
                ¿Olvidó su contraseña?
              </Link>
            </div>

            {error && (
              <div className="text-sm text-red-500 bg-red-50 p-3 rounded-md border border-red-200">
                {error}
              </div>
            )}

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Iniciando sesión...
                </>
              ) : (
                'Iniciar Sesión'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-gray-600">
            ¿Necesita ayuda? <span className="text-blue-600">Contactar soporte</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function LoginForm() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-gray-500">Cargando...</div>
        </div>
      }
    >
      <LoginFormInner />
    </Suspense>
  );
}
